import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Minus, Plus, Truck, RefreshCw, ShieldCheck, ChevronDown } from 'lucide-react';
import WishlistButton from '../components/WishlistButton';
import Reviews from '../components/Reviews';
import Stars from '../components/Stars';
import ProductCard from '../components/ProductCard';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import api, { formatPrice } from '../api';

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [openAccordion, setOpenAccordion] = useState('details');
  const { toast } = useToast();
  const { add } = useCart();

  useEffect(() => {
    setActiveImg(0); setQty(1);
    api.get(`/products/${id}`).then(r => {
      setProduct(r.data);
      api.get(`/products?category=${r.data.category}`).then(rr => {
        setRelated(rr.data.filter(p => p.id !== r.data.id).slice(0, 4));
      });
    }).catch(() => setProduct(null));
  }, [id]);

  if (!product) {
    return <main className="max-w-4xl mx-auto px-6 py-24 text-center"><p className="text-sm text-[#1a1a1a]/50">Loading…</p></main>;
  }

  const gallery = product.images?.length > 1 ? product.images : [product.image, product.image, product.image];

  const addToBag = () => {
    add(product, qty);
    toast({ title: 'Added to your bag', description: `${product.name} × ${qty}` });
  };

  return (
    <main>
      <div className="max-w-7xl mx-auto px-6 pt-10">
        <nav className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/50">
          <Link to="/" className="hover:text-[#C6410E]">Home</Link>
          <span className="mx-2">/</span>
          <Link to={`/shop/${product.category}`} className="hover:text-[#C6410E] capitalize">{product.category.replace('-', ' ')}</Link>
          <span className="mx-2">/</span>
          <span className="text-[#1a1a1a]">{product.name}</span>
        </nav>
      </div>

      <section className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="product-img-wrap aspect-[4/5] mb-4">
            <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-4 gap-3">
            {gallery.map((g, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden border ${i === activeImg ? 'border-[#C6410E]' : 'border-transparent'}`}>
                <img src={g} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="md:pt-6">
          {product.isNew && <p className="text-[11px] tracking-widest-plus uppercase text-[#C6410E] mb-4">Newly Made</p>}
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight mb-3">{product.name}</h1>
          <div className="flex items-center gap-4 mb-6">
            <p className="text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/60">{product.color}</p>
            {product.rating_count > 0 && (
              <a href="#reviews" data-testid="product-rating-link" className="flex items-center gap-2 text-xs text-[#1a1a1a]/70 hover:text-[#C6410E]">
                <Stars value={product.rating_avg} /> {product.rating_avg.toFixed(1)} ({product.rating_count})
              </a>
            )}
          </div>
          <p className="text-2xl mb-8">{formatPrice(product.price)}</p>
          <p className="text-[#1a1a1a]/75 leading-relaxed mb-8">{product.description}</p>

          <div className="mb-8">
            <p className="text-[11px] tracking-widest-plus uppercase mb-3">Colour — <span className="text-[#1a1a1a]/60">{product.color}</span></p>
            <div className="flex gap-3">
              {['#7B3F1E', '#1E1917', '#B08055', '#2A2D3E', '#3A4A2F'].map((c, i) => (
                <button key={i} className="w-8 h-8 rounded-full border-2 border-[#F4EEE4] ring-1 ring-black/10 hover:ring-[#C6410E]" style={{ background: c }} aria-label={`Colour ${i + 1}`} />
              ))}
            </div>
          </div>

          <div className="flex items-stretch gap-4 mb-6">
            <div className="flex items-center border border-black/30">
              <button className="px-4 py-3" onClick={() => setQty(q => Math.max(1, q - 1))}><Minus className="w-4 h-4" /></button>
              <span className="px-3 text-sm w-8 text-center">{qty}</span>
              <button className="px-4 py-3" onClick={() => setQty(q => q + 1)}><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={addToBag} className="btn-primary flex-1">Add to bag</button>
            <WishlistButton product={product} className="border border-black/30 px-4 hover:border-[#C6410E]" />
          </div>

          <div className="grid grid-cols-3 gap-4 py-6 border-y border-black/10 my-8 text-[11px] tracking-widest-plus uppercase text-[#1a1a1a]/70">
            <div className="flex flex-col items-center text-center gap-2"><Truck className="w-5 h-5 text-[#C6410E]" /> Free shipping</div>
            <div className="flex flex-col items-center text-center gap-2"><RefreshCw className="w-5 h-5 text-[#C6410E]" /> 14-day returns</div>
            <div className="flex flex-col items-center text-center gap-2"><ShieldCheck className="w-5 h-5 text-[#C6410E]" /> Lifetime repair</div>
          </div>

          <div className="border-t border-black/10">
            {[
              { id: 'details', label: 'Details & materials', content: (
                <ul className="list-disc pl-5 space-y-2">{(product.details || []).map((d, i) => <li key={i}>{d}</li>)}</ul>
              )},
              { id: 'care', label: 'Care', content: <p>Wipe with a soft dry cloth. Condition every six months with a neutral leather balm. Keep away from prolonged direct sunlight and water.</p> },
              { id: 'shipping', label: 'Shipping & returns', content: <p>Complimentary shipping across India on orders above &#8377;5,000. Made-to-order items dispatch in 2–4 weeks. Returns accepted within 14 days on unused pieces.</p> }
            ].map(a => (
              <div key={a.id} className="border-b border-black/10">
                <button onClick={() => setOpenAccordion(o => o === a.id ? '' : a.id)} className="w-full flex justify-between items-center py-5 text-left">
                  <span className="text-[11px] tracking-widest-plus uppercase">{a.label}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${openAccordion === a.id ? 'rotate-180' : ''}`} />
                </button>
                {openAccordion === a.id && <div className="pb-5 text-sm text-[#1a1a1a]/75 leading-relaxed">{a.content}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Reviews productId={product.id} />

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="font-serif-display text-3xl md:text-4xl mb-10">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </main>
  );
}
